import { getDashboardDivisions } from '@api/getDashboardDivisions';

import {
  DivisionsFormattedItemType,
  DivisionsItemType,
  ElectionSettings,
} from '@type/dashboard';
import { useEffect, useState } from 'react';
import { API_REFETCH_TIME, colorPalette } from '@containers/dashboard/options';
import { numberLanguageFormatter } from '@utils/utils';
import { useCustomParams } from './useCustomParams';

interface useDashboardDivisionsType {
  divisionsList: DivisionsFormattedItemType[];
  isLoading: boolean;
}

export const useDashboardDivisions = (): useDashboardDivisionsType => {
  const [divisionsList, setDivisionsList] = useState<
    DivisionsFormattedItemType[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiRefetch, setApiRefetch] = useState<boolean>(false);

  const { searchParams } = useCustomParams();
  const election_schedule_id = searchParams.get('election_schedule_id');

  useEffect(() => {
    setIsLoading(true);

    if (election_schedule_id) {
      getDashboardDivisions({ election_schedule_id })
        .then((res: any) => {
          const formattedRes = res?.data?.data?.map(
            (item: DivisionsItemType) => {
              const newItem: DivisionsFormattedItemType = {
                ...(item || {}),
                election_setting_name: [],
                election_setting_value: [],
              };

              const name: string[] = [];
              const value: {
                name: string;
                value: string;
                castedVote: string;
                totalVoters: string;
                doneVoteCenter: number;
                zillaName: string;
                itemStyle: { borderRadius: number[]; color: any };
              }[] = [];

              newItem?.election_setting?.forEach(
                (nestedItem: ElectionSettings) => {
                  name.push(
                    `${numberLanguageFormatter(nestedItem?.id || '')} - ${
                      nestedItem?.election_setting || ''
                    }`,
                  );

                  value.push({
                    name: `${numberLanguageFormatter(nestedItem?.id || '')} - ${
                      nestedItem?.election_setting || ''
                    }`,
                    value: `${nestedItem?.percentage || 0}`,
                    totalVoters: `${nestedItem?.total_voters}`,
                    castedVote: `${nestedItem?.total_vote_cast}`,
                    doneVoteCenter: nestedItem?.done_center_count || 0,
                    zillaName: nestedItem?.zilla_name || '',
                    itemStyle: {
                      color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                          {
                            offset: 0,
                            color: colorPalette[item?.id - 2][0],
                          },
                          {
                            offset: 1,
                            color: colorPalette[item?.id - 2][1],
                          },
                        ],
                        global: false,
                      },
                      borderRadius: [5, 5, 0, 0],
                    },
                  });
                },
              );

              newItem.election_setting_name = name;
              newItem.election_setting_value = value;

              return newItem;
            },
          );

          setDivisionsList(formattedRes || []);
          setIsLoading(false);
        })
        .catch((err) => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [apiRefetch, election_schedule_id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setApiRefetch((prev) => !prev);
    }, API_REFETCH_TIME * 1000);

    return () => {
      setApiRefetch(false);
      clearInterval(interval);
    };
  }, []);

  return {
    divisionsList,
    isLoading,
  };
};
